import { Box, Button, Divider, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import type { QuoteDetail } from '../../../types/quote';
import { formatQuoteAmount } from '../../../utils/quoteCurrency';

interface QuoteSummaryProps {
  quote: QuoteDetail;
  onDuplicate: () => void;
}

export const QuoteSummary = ({ quote, onDuplicate }: QuoteSummaryProps) => {
  // Amounts are stored in CAD; convert to the quote currency for display
  // (BUG-2026-0120 #6). CAD quotes pass through unchanged (rate 1.0).
  const fmt = (value: number) =>
    formatQuoteAmount(value, quote.Currency, quote.FxRateAtQuote);
  const cur = (quote.Currency || 'CAD').toUpperCase();

  return (
    <Box sx={{
      width: 200, borderLeft: '1px solid', borderColor: 'divider',
      p: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5,
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <SummaryRow label="Subtotal" value={fmt(quote.Subtotal)} />
        {quote.PmFeeAmount > 0 && (
          <SummaryRow label="PM Fee (10%)" value={fmt(quote.PmFeeAmount)} />
        )}
        {quote.DiscountAmount > 0 && (
          <SummaryRow label="Discount" value={`-${fmt(quote.DiscountAmount)}`} color="error.main" />
        )}
        {quote.TaxAmount > 0 && (
          <SummaryRow label="Tax" value={fmt(quote.TaxAmount)} />
        )}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>{`Total (${cur})`}</Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
          {fmt(quote.GrandTotal)}
        </Typography>
      </Box>

      <Divider />

      <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={onDuplicate}
        sx={{ textTransform: 'none', fontSize: '0.75rem', mt: 'auto' }}>
        Duplicate Quote
      </Button>
    </Box>
  );
};

const SummaryRow = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);
